import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { BOOKING_STATUS, type BookingStatus } from "@avestate/shared";
import { apiFetch } from "@/lib/api.js";
import { useAuth } from "@/auth/useAuth.js";

const Booking = z.object({
  id: z.string().uuid(),
  listing_id: z.string().uuid().nullable(),
  name: z.string(),
  email: z.string(),
  phone: z.string().nullable(),
  lang: z.string(),
  preferred_date: z.string().nullable(),
  preferred_slot: z.string().nullable(),
  message: z.string().nullable(),
  status: z.enum(BOOKING_STATUS),
  created_at: z.string(),
  listing: z.object({ slug: z.string(), title: z.unknown().nullable(), city: z.string().nullable() }).nullable().optional(),
});
export type Booking = z.infer<typeof Booking>;

const Response = z.object({ items: z.array(Booking) });

export function useBookings() {
  const { session } = useAuth();
  const token = session?.access_token;
  return useQuery({
    queryKey: ["bookings"],
    enabled: !!token,
    queryFn: async (): Promise<Booking[]> => {
      const r = await apiFetch("/api/v1/admin/bookings", token!, Response);
      return r.items;
    },
  });
}

export function usePatchBooking() {
  const qc = useQueryClient();
  const { session } = useAuth();
  const token = session?.access_token;
  return useMutation({
    mutationFn: ({ id, status }: { id: string; status: BookingStatus }) =>
      apiFetch(`/api/v1/admin/bookings/${id}`, token!, z.object({ id: z.string(), status: z.string() }), {
        method: "PATCH",
        body: { status },
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["bookings"] });
      // una visita gestita esce anche dalla vista urgenze in dashboard
      void qc.invalidateQueries({ queryKey: ["attention"] });
    },
  });
}
